/**
 * 时区转换工具
 *
 * 背景：服务器在中国，所有时间均按北京时间（UTC+8）记录并返回（格式如 "2026-08-06 12:00:00"，不带时区标识）。
 * 前端需要根据用户选择的时区，将北京时间换算为当地时间后再显示；
 * 查询条件（日期范围）则需要把用户选择的当地时间换算回北京时间后再提交给后端。
 *
 * 实现方式：使用浏览器原生 Intl.DateTimeFormat（支持 IANA 时区），无需引入额外依赖。
 */

const BEIJING_OFFSET_MS = 8 * 60 * 60 * 1000

/**
 * 获取当前生效的时区（用户手动选择的优先，否则默认北京时间）
 */
export function getTimezone () {
  return localStorage.getItem('timezone') || 'Asia/Shanghai'
}

/**
 * 检测用户系统所在的时区（IANA 名称），用于自动切换
 */
export function detectLocalTimezone () {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Shanghai'
  } catch (e) {
    return 'Asia/Shanghai'
  }
}

const TIME_REGEX = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/

/**
 * 解析服务器返回的北京时间字符串为时间戳（毫秒）
 * 支持 "YYYY-MM-DD HH:mm:ss" / "YYYY-MM-DDTHH:mm:ss" / "YYYY-MM-DD"
 */
function parseBeijingTime (timeStr) {
  if (timeStr === null || timeStr === undefined || timeStr === '') return NaN
  const m = TIME_REGEX.exec(String(timeStr).trim())
  if (!m) return NaN
  const utcMs = Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0))
  return utcMs - BEIJING_OFFSET_MS
}

/**
 * 获取指定时刻在指定时区下的各日期时间字段
 */
function getPartsInTimezone (epochMs, timeZone) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  })
  const parts = {}
  dtf.formatToParts(new Date(epochMs)).forEach(p => {
    parts[p.type] = p.value
  })
  return parts
}

/**
 * 将北京时间字符串转换为目标时区下的显示字符串
 * @param timeStr 服务器返回的北京时间字符串
 * @param timezone 目标时区（IANA），不传则取当前用户选择的时区
 * @param withSeconds 是否显示秒，默认 true
 * @returns {string} 形如 "2026-08-06 20:00:00"；无法解析时原样返回
 */
export function formatServerTime (timeStr, timezone, withSeconds = true) {
  if (timeStr === null || timeStr === undefined || timeStr === '') return timeStr
  const epoch = parseBeijingTime(timeStr)
  if (isNaN(epoch)) return timeStr
  const tz = timezone || getTimezone()
  let parts
  try {
    parts = getPartsInTimezone(epoch, tz)
  } catch (e) {
    // 时区名称非法时回退到北京时间
    parts = getPartsInTimezone(epoch, 'Asia/Shanghai')
  }
  const date = `${parts.year}-${parts.month}-${parts.day}`
  const time = withSeconds
    ? `${parts.hour}:${parts.minute}:${parts.second}`
    : `${parts.hour}:${parts.minute}`
  return `${date} ${time}`
}

/**
 * 将绝对时间戳（毫秒数或 ISO 字符串，如 actuator/httptrace 的 timestamp）格式化为目标时区下的显示字符串
 * @param value 时间戳（number）或可被 Date.parse 解析的字符串
 * @param timezone 目标时区（IANA），不传则取当前用户选择的时区
 * @param withSeconds 是否显示秒，默认 true
 * @returns {string} 形如 "2026-08-06 20:00:00"；无法解析时原样返回
 */
export function formatEpochTime (value, timezone, withSeconds = true) {
  if (value === null || value === undefined || value === '') return value
  const epoch = typeof value === 'number' ? value : Date.parse(String(value))
  if (isNaN(epoch)) return value
  const tz = timezone || getTimezone()
  let parts
  try {
    parts = getPartsInTimezone(epoch, tz)
  } catch (e) {
    parts = getPartsInTimezone(epoch, 'Asia/Shanghai')
  }
  const date = `${parts.year}-${parts.month}-${parts.day}`
  const time = withSeconds
    ? `${parts.hour}:${parts.minute}:${parts.second}`
    : `${parts.hour}:${parts.minute}`
  return `${date} ${time}`
}

/**
 * 获取指定时刻下，指定时区与 UTC 的偏移量（毫秒）
 */
function timezoneOffsetMs (timeZone, date) {
  const parts = getPartsInTimezone(date.getTime(), timeZone)
  const asUTC = Date.UTC(
    +parts.year, +parts.month - 1, +parts.day,
    +parts.hour, +parts.minute, +parts.second
  )
  return asUTC - Math.floor(date.getTime() / 1000) * 1000
}

/**
 * 把"指定时区下的墙上时钟时间字符串"换算为时间戳（毫秒）
 * 例：zonedTimeToEpoch('2026-08-06 00:00:00', 'Asia/Bangkok')
 */
function zonedTimeToEpoch (dateTimeStr, timeZone) {
  const m = TIME_REGEX.exec(String(dateTimeStr).trim())
  if (!m) return NaN
  const utcGuess = Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0))
  const offset = timezoneOffsetMs(timeZone, new Date(utcGuess))
  return utcGuess - offset
}

/**
 * 时间戳转北京时间字符串 "YYYY-MM-DD HH:mm:ss"
 */
function epochToBeijingString (epochMs) {
  const p = getPartsInTimezone(epochMs, 'Asia/Shanghai')
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second}`
}

/**
 * 将用户在"当地时区"选择的日期范围，换算为北京时间的时间范围，用于向后端提交查询条件
 * 例：用户在曼谷（UTC+7）选择 2026-08-06 ~ 2026-08-06，
 *     换算后为北京时间 2026-08-06 01:00:00 ~ 2026-08-07 01:00:00
 * @param startDate 开始日期 "YYYY-MM-DD"
 * @param endDate 结束日期 "YYYY-MM-DD"
 * @param timezone 用户时区（IANA），不传则取当前用户选择的时区
 * @returns {{from: string, to: string}} 北京时间时间范围
 */
export function convertLocalRangeToBeijing (startDate, endDate, timezone) {
  const tz = timezone || getTimezone()
  const fromEpoch = zonedTimeToEpoch(`${startDate} 00:00:00`, tz)
  const toEpoch = zonedTimeToEpoch(`${endDate} 23:59:59`, tz)
  return {
    from: epochToBeijingString(fromEpoch),
    to: epochToBeijingString(toEpoch)
  }
}

export default {
  getTimezone,
  detectLocalTimezone,
  formatServerTime,
  formatEpochTime,
  convertLocalRangeToBeijing
}



// WEBPACK FOOTER //
// ./src/utils/timezoneUtil.js